import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Box, Typography, IconButton, List, ListItem, ListItemText,
  Chip, Alert, Divider, Tooltip, Switch, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  TextFields as TextIcon,
  Numbers as NumberIcon,
  CalendarToday as DateIcon,
  CheckBox as CheckboxIcon,
  List as SelectIcon
} from '@mui/icons-material';
import { customFieldService } from '../services/api';

const FIELD_TYPES = [
  { value: 'text',     label: 'Texte',     icon: <TextIcon fontSize="small" /> },
  { value: 'number',   label: 'Nombre',    icon: <NumberIcon fontSize="small" /> },
  { value: 'date',     label: 'Date',      icon: <DateIcon fontSize="small" /> },
  { value: 'select',   label: 'Liste',     icon: <SelectIcon fontSize="small" /> },
  { value: 'checkbox', label: 'Case à cocher', icon: <CheckboxIcon fontSize="small" /> },
];

const TYPE_COLORS = { text: '#1976d2', number: '#7b1fa2', date: '#0288d1', select: '#388e3c', checkbox: '#f57c00' };

export default function CustomFieldsManager({ open, onClose, projectId, userRole, onFieldsChange }) {
  const [fields, setFields]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [showForm, setShowForm]     = useState(false);
  const [newField, setNewField]     = useState({ name: '', field_type: 'text', options: '', required: false });
  const [saving, setSaving]         = useState(false);

  const canEdit = ['owner', 'admin'].includes(userRole);

  useEffect(() => {
    if (open && projectId) fetchFields();
  }, [open, projectId]);

  const fetchFields = async () => {
    setLoading(true);
    try {
      const res = await customFieldService.getProjectFields(projectId);
      setFields(res.data.fields || []);
    } catch (e) {
      setError('Erreur lors du chargement des champs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newField.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const options = newField.field_type === 'select'
        ? newField.options.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      await customFieldService.createField(projectId, {
        name: newField.name.trim(),
        field_type: newField.field_type,
        options,
        required: newField.required,
        order_index: fields.length
      });
      setNewField({ name: '', field_type: 'text', options: '', required: false });
      setShowForm(false);
      await fetchFields();
      onFieldsChange?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fieldId) => {
    try {
      await customFieldService.deleteField(fieldId);
      await fetchFields();
      onFieldsChange?.();
    } catch (e) {
      setError('Erreur lors de la suppression');
    }
  };

  const typeInfo = (type) => FIELD_TYPES.find(t => t.value === type) || FIELD_TYPES[0];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SelectIcon color="primary" />
          Champs personnalisés
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Liste des champs existants */}
        {fields.length === 0 && !loading && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            Aucun champ personnalisé pour ce projet.
          </Typography>
        )}

        <List disablePadding>
          {fields.map((field, idx) => (
            <React.Fragment key={field.id}>
              {idx > 0 && <Divider />}
              <ListItem
                secondaryAction={
                  canEdit && (
                    <Tooltip title="Supprimer ce champ">
                      <IconButton size="small" color="error" onClick={() => handleDelete(field.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                }
                sx={{ pl: 0 }}
              >
                <DragIcon sx={{ color: 'text.disabled', mr: 1, cursor: 'grab' }} />
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">{field.name}</Typography>
                      {field.required && <Chip label="Requis" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        icon={typeInfo(field.field_type).icon}
                        label={typeInfo(field.field_type).label}
                        size="small"
                        sx={{ bgcolor: `${TYPE_COLORS[field.field_type]}18`, color: TYPE_COLORS[field.field_type], height: 20, fontSize: '0.7rem' }}
                      />
                      {field.field_type === 'select' && field.options?.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {field.options.join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>

        {/* Formulaire d'ajout */}
        {canEdit && (
          <Box sx={{ mt: 2 }}>
            {!showForm ? (
              <Button startIcon={<AddIcon />} onClick={() => setShowForm(true)} variant="outlined" fullWidth>
                Ajouter un champ
              </Button>
            ) : (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mt: 1 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">Nouveau champ</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Nom du champ"
                    value={newField.name}
                    onChange={e => setNewField({ ...newField, name: e.target.value })}
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder="ex: Client, Budget, N° devis..."
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={newField.field_type}
                      onChange={e => setNewField({ ...newField, field_type: e.target.value })}
                    >
                      {FIELD_TYPES.map(t => (
                        <MenuItem key={t.value} value={t.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {t.icon} {t.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {newField.field_type === 'select' && (
                    <TextField
                      label="Options (séparées par des virgules)"
                      value={newField.options}
                      onChange={e => setNewField({ ...newField, options: e.target.value })}
                      size="small"
                      fullWidth
                      placeholder="Option 1, Option 2, Option 3"
                      helperText="Séparez chaque option par une virgule"
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newField.required}
                        onChange={e => setNewField({ ...newField, required: e.target.checked })}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Champ requis</Typography>}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" size="small" onClick={handleCreate} disabled={!newField.name.trim() || saving}>
                      {saving ? 'Création...' : 'Créer'}
                    </Button>
                    <Button size="small" onClick={() => { setShowForm(false); setNewField({ name: '', field_type: 'text', options: '', required: false }); }}>
                      Annuler
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}
